import { useCallback, useState } from 'react'
import { toast } from 'sonner'
import type { RowsSelection } from '@/components/structured-ingest/RowsDialog'
import { usePromoteRows } from './hooks/use-structured-ingest'
import { landRowExcerpt } from './row-excerpt-object-set'

/**
 * Marking rows in a rows dialog and promoting them into the graph as one RowExcerpt,
 * which then lands where the analyst is looking (active investigation or a draft).
 * `table` is `<analysisId>/<tableId>` — null disables marking.
 */
export function useRowsSelection(caseId: string, table: string | null): RowsSelection | null {
  const [selected, setSelected] = useState<Set<number>>(() => new Set())
  const promote = usePromoteRows(caseId)
  const onToggle = useCallback((position: number) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(position)) next.delete(position)
      else next.add(position)
      return next
    })
  }, [])
  const onReplace = useCallback((positions: number[]) => setSelected(new Set(positions)), [])
  const onClear = useCallback(() => setSelected(new Set()), [])
  const onPromote = useCallback(
    (positions: number[], note: string) => {
      if (!table) return
      const [analysisId, tableId] = table.split('/')
      if (!analysisId || !tableId) return
      promote.mutate(
        { analysisId, tableId, positions, note },
        {
          onSuccess: excerpt => {
            const landing = landRowExcerpt(excerpt, caseId)
            setSelected(new Set())
            toast.success(
              landing === 'draft'
                ? `Promoted ${excerpt.rowCount} rows — started a draft investigation`
                : `Promoted ${excerpt.rowCount} rows into your investigation`
            )
          },
          onError: (error: Error) => toast.error(`Could not promote: ${error.message}`),
        }
      )
    },
    [caseId, promote, table]
  )
  if (!table) return null
  return { selected, onToggle, onReplace, onClear, onPromote, promoting: promote.isPending }
}
